import forEach from 'lodash/forEach';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import some from 'lodash/some';
import {
  DASHBOARD_PERSPECTIVE__PATIENTS,
  PROJECT_DASHBOARD_TYPE__TIMELINE,
  PROJECT_DASHBOARD_TYPE__DETAILS,
  PROJECT_DASHBOARD_TYPE__EAPP,
  PROJECT_DASHBOARD_TYPE__EAPP_DASHBOARD_V2,
} from '../../common/constants';
import {
  getPerspective,
  getSpecialization,
  hasSpecialization,
} from './dashboards';

// Dashboard types the server publishes only when the matching permission is
// granted (`project.MILESTONE.review` for `timeline`, `project.EAPP.review` for
// the eAPP types) - see getViewableDashboards.js on the server. The published
// set is therefore the client's only signal of those permissions, and the
// `_view` query param is user-controlled: honouring `_view=timeline` when no
// timeline dashboard was published would render a view the server just denied.
// Keep this list in sync with GATED_TYPES on the server.
//
// Specialization views (a single patient, participation, activity or response)
// are deliberately not covered here: they render per-record components guarded
// by their own server-side permissions rather than by a dashboard document.
const GATED_TYPES = [
  PROJECT_DASHBOARD_TYPE__TIMELINE,
  PROJECT_DASHBOARD_TYPE__EAPP,
  PROJECT_DASHBOARD_TYPE__EAPP_DASHBOARD_V2,
];

const getPreset = (dashboard) => {
  return {
    title: dashboard.title,
    id: dashboard._id,
    settings: cloneDeep(dashboard.settings),
  };
};

const createGetViewParams = (dashboards) => {
  // Every project always has a timeline dashboard, so an absent one never means
  // "this project has none" - it means the server withheld it because the user
  // lacks `project.MILESTONE.review`. Only default to the timeline view when one
  // was actually published; otherwise leave the type undefined and let
  // ProjectTabs render nothing rather than the denied view.
  const hasTimelineDashboard = some(dashboards, {
    type: PROJECT_DASHBOARD_TYPE__TIMELINE,
  });
  const byType = {};
  const idToPresetNo = {};
  forEach(dashboards, (dashboard) => {
    const { _id, type } = dashboard;
    if (!byType[type]) {
      byType[type] = [];
    }
    idToPresetNo[_id] = byType[type].length + 1;
    byType[type].push(dashboard);
  });
  const getDashboard = ({
    type,
    settings,
    perspective,
    presetNo,
    dashboardId,
  }) => {
    return find(dashboards, (dashboard) => {
      if (
        (type && dashboard.type !== type) ||
        (dashboardId && dashboard._id !== dashboardId) ||
        (typeof presetNo === 'number' &&
          idToPresetNo[dashboard._id] !== presetNo)
      ) {
        return false;
      }
      if (
        perspective &&
        dashboard.perspective &&
        perspective !== dashboard.perspective
      ) {
        return false;
      }
      const specialization = getSpecialization(settings);
      if (perspective || dashboard.perspective) {
        if (
          !hasSpecialization(
            dashboard.type,
            perspective || dashboard.perspective,
            specialization,
          )
        ) {
          return false;
        }
      } else if (!getPerspective(dashboard.type, specialization)) {
        return false;
      }
      return true;
    });
  };
  return (rawParams) => {
    const specialization = getSpecialization(rawParams.settings);
    let dashboard = getDashboard(rawParams);
    const wantsWithheldGatedType =
      !dashboard && !specialization && GATED_TYPES.includes(rawParams.type);
    if (wantsWithheldGatedType) {
      // The requested type was not published for this user. Resolve the view the
      // same way a plain visit to the project does - i.e. land on the first
      // dashboard they are allowed - instead of rendering the denied type.
      dashboard = getDashboard({
        ...rawParams,
        type: undefined,
        presetNo: undefined,
      });
    }
    if (dashboard) {
      return {
        ...rawParams,
        projectId: dashboard.projectId,
        type: dashboard.type,
        perspective:
          rawParams.perspective ||
          dashboard.perspective ||
          getPerspective(dashboard.type, specialization),
        presetNo: idToPresetNo[dashboard._id],
        preset: getPreset(dashboard),
      };
    }
    const {
      perspective = DASHBOARD_PERSPECTIVE__PATIENTS,
      // NOTE: The specialization branch stays unconditional - per-record views
      //       render without a dashboard document and are guarded by their own
      //       server-side permissions.
      type: requestedType = specialization
        ? PROJECT_DASHBOARD_TYPE__DETAILS
        : (hasTimelineDashboard && PROJECT_DASHBOARD_TYPE__TIMELINE) ||
          undefined,
    } = rawParams;
    // A destructuring default only fires when the property is undefined, so a
    // `_view=<gated type>` URL passed straight through here whenever the
    // re-resolution above found nothing to fall back to - which is exactly the
    // case of a user denied every dashboard. Drop the type instead: ProjectTabs
    // renders null for an unknown type, and getLocationObject omits `_view`.
    const type = wantsWithheldGatedType ? undefined : requestedType;
    return {
      ...rawParams,
      type,
      perspective,
    };
  };
};

export default createGetViewParams;
